import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { usePlayer } from "@/context/PlayerContext";
import { SONGS } from "@/data/eiffel65";
import { resolveSongCover, ALBUM_COVERS } from "@/data/covers";
import {
  Play, Pause, SkipForward, SkipBack, Shuffle, Repeat, Square, X, Volume2, VolumeX,
} from "lucide-react";

/**
 * Winamp-style retro player panel. Anchored ABOVE the slim GlobalPlayer
 * bar when expanded. Pure visual layer — all state still comes from
 * PlayerContext (YT IFrame), so audio output is identical.
 */

const LCD = ({ children, className = "", testId }) => (
  <span
    data-testid={testId}
    className={`font-mono tabular-nums ${className}`}
    style={{
      color: "#7CFFCB",
      textShadow: "0 0 4px rgba(124,255,203,0.6), 0 0 12px rgba(124,255,203,0.25)",
      letterSpacing: "0.04em",
    }}
  >
    {children}
  </span>
);

const ChromeButton = ({ children, active, onClick, "aria-label": ariaLabel, testId, big = false }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    aria-label={ariaLabel}
    className={`relative grid place-items-center rounded-md transition-all ${
      big ? "w-12 h-9" : "w-9 h-8"
    } ${
      active
        ? "text-[#7CFFCB]"
        : "text-[#B6E1FF] hover:text-white"
    }`}
    style={{
      background:
        "linear-gradient(180deg, #2A4B73 0%, #11253D 55%, #0A1A2C 100%)",
      border: "1px solid rgba(0,175,255,0.35)",
      boxShadow:
        active
          ? "inset 0 1px 0 rgba(255,255,255,0.18), 0 0 10px rgba(124,255,203,0.45)"
          : "inset 0 1px 0 rgba(255,255,255,0.18), 0 1px 2px rgba(0,0,0,0.5)",
    }}
  >
    {children}
  </button>
);

function EqBars({ active }) {
  // 14 vertical bars with random "frequency"-like heights, animated via CSS.
  const bars = useMemo(() => Array.from({ length: 14 }, (_, i) => ({
    delay: (i * 0.073) % 0.7,
    base: 0.25 + Math.random() * 0.55,
  })), []);
  return (
    <div className="flex items-end gap-[3px] h-12 px-2 py-1 rounded-md" style={{
      background: "linear-gradient(180deg, #04101F 0%, #061C32 100%)",
      border: "1px solid rgba(0,175,255,0.25)",
      boxShadow: "inset 0 0 12px rgba(0,175,255,0.18)",
    }}>
      {bars.map((b, i) => (
        <span
          key={i}
          className="winamp-eq-bar"
          style={{
            width: 4,
            height: `${Math.round(b.base * 100)}%`,
            background: "linear-gradient(180deg, #FF3B6B 0%, #FFD23B 35%, #7CFFCB 75%, #00AFFF 100%)",
            boxShadow: "0 0 4px rgba(0,175,255,0.5)",
            animationDelay: `${b.delay}s`,
            animationPlayState: active ? "running" : "paused",
            opacity: active ? 1 : 0.35,
          }}
        />
      ))}
    </div>
  );
}

function Marquee({ text }) {
  // Always render text twice so we get a seamless loop.
  return (
    <div className="overflow-hidden flex-1 rounded-md py-1 px-2"
      style={{
        background: "#020A18",
        border: "1px solid rgba(0,175,255,0.35)",
        boxShadow: "inset 0 0 14px rgba(0,175,255,0.2)",
      }}
    >
      <div className="winamp-marquee whitespace-nowrap text-[13px] font-mono font-bold tracking-wider"
        style={{
          color: "#7CFFCB",
          textShadow: "0 0 4px rgba(124,255,203,0.7), 0 0 14px rgba(124,255,203,0.25)",
        }}
      >
        <span className="pr-12">{text}</span>
        <span className="pr-12">{text}</span>
      </div>
    </div>
  );
}

function fmt(s) {
  const sec = Math.max(0, Math.floor(s || 0));
  return `${Math.floor(sec / 60).toString().padStart(2, "0")}:${(sec % 60).toString().padStart(2, "0")}`;
}

export default function WinampPanel({ open, onClose }) {
  const {
    currentSong, currentSongId, playSong,
    isPlaying, currentTime, duration, volume, muted,
    togglePlay, next, prev, seek, setVolume, toggleMute,
    shuffle, setShuffle, repeat, setRepeat,
  } = usePlayer();

  const [seekHover, setSeekHover] = useState(null);
  const seekRef = useRef(null);

  // ESC closes panel
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !currentSong) return null;

  const officialCover = resolveSongCover(currentSong, ALBUM_COVERS);
  const coverImg = officialCover?.src || (currentSong.youtubeId
    ? `https://i.ytimg.com/vi/${currentSong.youtubeId}/mqdefault.jpg`
    : currentSong.cover);

  const dur = Math.max(0, duration || 0);
  const elapsed = Math.max(0, Math.min(dur || 1, currentTime || 0));
  const progress = dur > 0 ? (elapsed / dur) * 100 : 0;

  const marqueeText = `${currentSong.title.toUpperCase()}  •  EIFFEL 65  •  ${(currentSong.album || "Single").toUpperCase()}  •  ${currentSong.year || ""}  •  E65 RADIO`;

  // Compact queue (next 5 tracks after current)
  const queue = (() => {
    const arr = SONGS.filter((s) => s.youtubeId);
    const i = Math.max(0, arr.findIndex((s) => s.id === currentSongId));
    const out = [];
    for (let k = 1; k <= 6; k++) out.push(arr[(i + k) % arr.length]);
    return out;
  })();

  return (
    <AnimatePresence>
      <motion.div
        key="winamp-panel"
        initial={{ y: 24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 24, opacity: 0 }}
        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
        data-testid="winamp-panel"
        className="fixed left-1/2 -translate-x-1/2 bottom-[88px] sm:bottom-[80px] z-[60] w-[min(96vw,720px)]"
      >
        <div
          className="rounded-2xl overflow-hidden shadow-2xl"
          style={{
            background:
              "linear-gradient(180deg, #0F2540 0%, #08182B 50%, #04101F 100%)",
            border: "1px solid rgba(0,175,255,0.4)",
            boxShadow:
              "0 24px 60px rgba(0,175,255,0.25), inset 0 1px 0 rgba(255,255,255,0.08)",
          }}
        >
          {/* Title bar */}
          <div className="flex items-center justify-between px-3 py-1.5"
            style={{
              background:
                "linear-gradient(180deg, #1E3F69 0%, #0F2540 100%)",
              borderBottom: "1px solid rgba(0,175,255,0.3)",
            }}
          >
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                <span className="w-2 h-2 rounded-full" style={{ background: "#FF3B6B", boxShadow: "0 0 6px #FF3B6B" }}/>
                <span className="w-2 h-2 rounded-full" style={{ background: "#FFD23B", boxShadow: "0 0 6px #FFD23B" }}/>
                <span className="w-2 h-2 rounded-full" style={{ background: "#7CFFCB", boxShadow: "0 0 6px #7CFFCB" }}/>
              </div>
              <span className="text-[9px] tracking-[0.4em] uppercase font-bold"
                style={{ color: "#B6E1FF", textShadow: "0 0 6px rgba(0,175,255,0.5)" }}
              >
                E65 · Winamp Mode
              </span>
            </div>
            <button
              data-testid="winamp-close"
              onClick={onClose}
              aria-label="Close Winamp panel"
              className="w-6 h-6 grid place-items-center rounded text-white/55 hover:text-white hover:bg-white/10"
            >
              <X size={12}/>
            </button>
          </div>

          {/* Display row: cover + LCDs + EQ */}
          <div className="grid grid-cols-12 gap-3 p-3">
            {/* Cover */}
            <div className="col-span-3 sm:col-span-2">
              <div className="rounded-md overflow-hidden border" style={{
                borderColor: "rgba(0,175,255,0.4)",
                boxShadow: "0 0 12px rgba(0,175,255,0.3), inset 0 0 0 1px rgba(255,255,255,0.05)",
              }}>
                <img src={coverImg} alt={currentSong.title} className="w-full h-auto aspect-square object-cover"/>
              </div>
            </div>

            {/* Center: LCDs + marquee */}
            <div className="col-span-9 sm:col-span-7 flex flex-col gap-2">
              {/* LCD time + bitrate + kHz */}
              <div className="flex items-center gap-2">
                <div className="px-2.5 py-1 rounded-md font-mono text-[22px] font-bold leading-none"
                  style={{
                    background: "#020A18",
                    border: "1px solid rgba(0,175,255,0.35)",
                    boxShadow: "inset 0 0 12px rgba(0,175,255,0.18)",
                  }}
                >
                  <LCD testId="winamp-elapsed">{fmt(elapsed)}</LCD>
                </div>
                <div className="hidden sm:flex flex-col items-start text-[9px] gap-0.5">
                  <LCD>128 kbps</LCD>
                  <LCD>44 kHz</LCD>
                </div>
                <div className="flex-1"/>
                <div className="hidden sm:block">
                  <EqBars active={isPlaying}/>
                </div>
              </div>

              {/* Marquee */}
              <Marquee text={marqueeText}/>

              {/* Seek bar */}
              <div className="relative">
                <div
                  ref={seekRef}
                  data-testid="winamp-seek"
                  className="relative h-3 rounded-full cursor-pointer overflow-hidden"
                  style={{
                    background:
                      "linear-gradient(180deg, #020A18 0%, #04162B 100%)",
                    border: "1px solid rgba(0,175,255,0.35)",
                    boxShadow: "inset 0 0 6px rgba(0,0,0,0.6)",
                  }}
                  onMouseMove={(e) => {
                    if (!dur) return;
                    const r = e.currentTarget.getBoundingClientRect();
                    const pct = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
                    setSeekHover(pct * dur);
                  }}
                  onMouseLeave={() => setSeekHover(null)}
                  onClick={(e) => {
                    if (!dur) return;
                    const r = e.currentTarget.getBoundingClientRect();
                    const pct = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
                    seek(pct * dur);
                  }}
                >
                  <div
                    className="absolute inset-y-0 left-0"
                    style={{
                      width: `${progress}%`,
                      background:
                        "linear-gradient(90deg, #00AFFF 0%, #7CFFCB 100%)",
                      boxShadow: "0 0 10px rgba(124,255,203,0.5)",
                    }}
                  />
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full"
                    style={{
                      left: `calc(${progress}% - 6px)`,
                      background:
                        "radial-gradient(circle at 30% 30%, #FFFFFF 0%, #7CFFCB 60%, #00AFFF 100%)",
                      boxShadow: "0 0 8px #7CFFCB",
                    }}
                  />
                </div>
                <div className="flex justify-between mt-1 text-[10px] font-mono">
                  <LCD>{fmt(elapsed)}</LCD>
                  <LCD>{seekHover !== null ? fmt(seekHover) : fmt(dur)}</LCD>
                </div>
              </div>
            </div>

            {/* Right: volume + EQ mobile */}
            <div className="col-span-12 sm:col-span-3 flex sm:flex-col items-center sm:items-end gap-2">
              <div className="sm:hidden flex-1">
                <EqBars active={isPlaying}/>
              </div>
              <div className="flex items-center gap-2 w-full justify-end">
                <button
                  data-testid="winamp-mute"
                  onClick={toggleMute}
                  aria-label="Mute"
                  className="text-[#B6E1FF] hover:text-white"
                >
                  {muted || volume === 0 ? <VolumeX size={14}/> : <Volume2 size={14}/>}
                </button>
                <input
                  data-testid="winamp-volume"
                  type="range"
                  min="0"
                  max="100"
                  value={muted ? 0 : volume}
                  onChange={(e) => setVolume(Number(e.target.value))}
                  aria-label="Volume"
                  className="winamp-range flex-1 sm:w-32"
                />
              </div>
              <div className="hidden sm:block text-right">
                <LCD className="text-[10px]">VOL {muted ? 0 : volume}%</LCD>
              </div>
            </div>
          </div>

          {/* Transport row */}
          <div className="flex items-center gap-1.5 px-3 pb-3">
            <ChromeButton testId="winamp-shuffle" active={shuffle} onClick={() => setShuffle(!shuffle)} aria-label="Shuffle">
              <Shuffle size={14}/>
            </ChromeButton>
            <ChromeButton testId="winamp-prev" onClick={prev} aria-label="Previous">
              <SkipBack size={14}/>
            </ChromeButton>
            <ChromeButton testId="winamp-play" onClick={togglePlay} aria-label={isPlaying ? "Pause" : "Play"} big>
              {isPlaying ? <Pause size={16} fill="currentColor"/> : <Play size={16} fill="currentColor"/>}
            </ChromeButton>
            <ChromeButton testId="winamp-stop" onClick={() => seek(0)} aria-label="Restart">
              <Square size={12} fill="currentColor"/>
            </ChromeButton>
            <ChromeButton testId="winamp-next" onClick={next} aria-label="Next">
              <SkipForward size={14}/>
            </ChromeButton>
            <ChromeButton testId="winamp-repeat" active={repeat} onClick={() => setRepeat(!repeat)} aria-label="Repeat">
              <Repeat size={14}/>
            </ChromeButton>
            <div className="flex-1"/>
            <div className="text-[9px] uppercase tracking-[0.3em] font-bold" style={{ color: "#B6E1FF" }}>
              Track {Math.max(1, (SONGS.filter(s=>s.youtubeId).findIndex(s=>s.id===currentSongId)+1))} of {SONGS.filter(s=>s.youtubeId).length}
            </div>
          </div>

          {/* Queue strip */}
          <div className="border-t" style={{ borderColor: "rgba(0,175,255,0.2)" }}>
            <div className="px-3 py-2 text-[9px] uppercase tracking-[0.3em] font-bold" style={{ color: "#B6E1FF" }}>
              Up Next
            </div>
            <div className="flex gap-2 px-3 pb-3 overflow-x-auto winamp-scroll">
              {queue.map((s) => {
                const off = resolveSongCover(s, ALBUM_COVERS);
                const cov = off?.src || (s.youtubeId ? `https://i.ytimg.com/vi/${s.youtubeId}/mqdefault.jpg` : s.cover);
                return (
                  <button
                    key={s.id}
                    data-testid={`winamp-queue-${s.id}`}
                    onClick={() => playSong(s.id)}
                    className="shrink-0 w-32 text-left rounded-md overflow-hidden transition hover:scale-[1.02]"
                    style={{
                      background: "linear-gradient(180deg, #0B1F36 0%, #061525 100%)",
                      border: "1px solid rgba(0,175,255,0.25)",
                    }}
                  >
                    <img src={cov} alt={s.title} className="w-full aspect-video object-cover"/>
                    <div className="px-2 py-1.5">
                      <div className="text-[11px] font-bold text-white truncate">{s.title}</div>
                      <div className="text-[9px] uppercase tracking-wider truncate" style={{ color: "#B6E1FF" }}>{s.year} · {s.album}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
